import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SmartActionButton.css';
// Updated: 2025-10-07 - Translation keys fixed

const SmartActionButton = ({ 
  selectedNode, 
  nodeName, 
  nodeType, 
  nodeDescription, 
  technology,
  saveNode, 
  updateNodeSimple,
  loading,
  resetForm
}) => {
  const { t, i18n } = useTranslation();
  
  // Force reload translations in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      i18n.reloadResources();
    }
  }, [i18n]);
  
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [showConfidenceTooltip, setShowConfidenceTooltip] = useState(false);

  // Contexte intelligent basé sur des critères réels
  const getActionContext = () => {
    let confidence = 100;
    
    // Critères de calcul de confiance
    
    // 1. Complétude des données (30 points max)
    if (!(nodeName || "").trim()) confidence -= 30;
    else if ((nodeName || "").trim().length < 3) confidence -= 15;
    
    if (!nodeDescription) confidence -= 10;
    else if (nodeDescription.length < 10) confidence -= 5;
    
    if (!nodeType && !selectedNode) confidence -= 10;
    if (!technology) confidence -= 5;
    
    // 2. Cohérence et qualité (25 points max)
    if (selectedNode) {
      // Pour une mise à jour
      if (selectedNode.name !== nodeName) confidence -= 5; // Changement de nom = risque
      if (selectedNode.type !== nodeType) confidence -= 10; // Changement de type = risque élevé
      if (['Core', 'API', 'Database', 'Authentication'].includes(selectedNode.type)) {
        confidence -= 10; // Nœud critique = moins de confiance
      }
    }
    
    // 3. Facteurs techniques (20 points max)
    const hasMetadata = nodeDescription && nodeDescription.includes('metadata');
    const hasSchema = nodeDescription && nodeDescription.includes('schema');
    if (!hasMetadata) confidence -= 10;
    if (!hasSchema) confidence -= 10;
    
    // 4. Bonus pour nœuds bien documentés (15 points max)
    if (nodeDescription && nodeDescription.length > 50) confidence += 5;
    if (technology && technology.length > 3) confidence += 5;
    if (nodeName && nodeName.length > 5) confidence += 5;
    
    // S'assurer que la confiance reste dans les limites
    confidence = Math.max(60, Math.min(100, confidence));
    
    return {
      confidence,
      primaryAction: saveNode,
      risk: selectedNode ? 'medium' : 'low'
    };
  };

  // Déterminer si les boutons doivent être activés
  const isFormValid = () => {
    // Toujours besoin d'un nom
    if (!(nodeName || "").trim()) return false;
    
    // Pour un nouveau nœud, il faut aussi un type
    if (!selectedNode && !nodeType) return false;
    
    // Pour un nœud existant, pas besoin de type (déjà défini)
    return true;
  };

  const actionContext = getActionContext();
  const isLoadingState = loading || localLoading;
  const buttonDisabled = isLoadingState || !isFormValid();
  
  // Gestion des actions avec confirmation
  const handleActionWithConfirmation = async (action, actionType) => {
    // Simuler la détection d'impact (à adapter selon votre logique métier)
    const hasHighImpact = selectedNode && actionType === 'simple' && (
      selectedNode.type !== nodeType || 
      selectedNode.name !== nodeName ||
      ['Core', 'API', 'Database', 'Authentication'].includes(selectedNode.type)
    );
    
    if (hasHighImpact) {
      setPendingAction(() => action);
      setShowConfirmation(true);
    } else {
      // Déclencher le loading immédiatement
      setLocalLoading(true);
      try {
        await action();
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const confirmAction = async () => {
    if (pendingAction) {
      setLocalLoading(true);
      try {
        await pendingAction();
        setPendingAction(null);
      } finally {
        setLocalLoading(false);
      }
    }
    setShowConfirmation(false);
  };

  const cancelAction = () => {
    setPendingAction(null);
    setShowConfirmation(false);
  };

  if (!actionContext) return null;

  return (
    <div className="smart-impact-analysis-container">
      {/* Header explicatif */}
      <div className="impact-analysis-header">
        <div className="analysis-icon">⚖️</div>
        <div className="analysis-info">
          <h4 className="analysis-title">{t('saveOptionsTitle')}</h4>
          <p className="analysis-subtitle">
            {t('saveOptionsSubtitle')}
            {!isFormValid() && (
              <span className="validation-hint">
                {!(nodeName || "").trim() ? t('validationNameRequired') : ""}
                {!selectedNode && !nodeType ? t('validationTypeRequired') : ""}
              </span>
            )}
          </p>
        </div>
        <div className="confidence-indicator">
          <div 
            className="confidence-circle"
            onMouseEnter={() => setShowConfidenceTooltip(true)}
            onMouseLeave={() => setShowConfidenceTooltip(false)}
            style={{ cursor: 'help' }}
          >
            <span className="confidence-value">{actionContext.confidence}%</span>
          </div>
          <span className="confidence-label">{t('confidence')}</span>
          
          {/* Info-bulle explicative */}
          {showConfidenceTooltip && (
            <div className="confidence-tooltip">
              <div className="tooltip-header">
                <span className="tooltip-icon">📊</span>
                <strong>{t('tooltipConfidenceCalculation')}</strong>
              </div>
              <div className="tooltip-content">
                <p>{t('tooltipReliabilityExplanation')}</p>
                <div className="tooltip-factors">
                  <div className="tooltip-factor">
                    <span className="factor-icon">✓</span>
                    <span>{t('tooltipFactor1')}</span>
                  </div>
                  <div className="tooltip-factor">
                    <span className="factor-icon">✓</span>
                    <span>{t('tooltipFactor2')}</span>
                  </div>
                  <div className="tooltip-factor">
                    <span className="factor-icon">✓</span>
                    <span>{t('tooltipFactor3')}</span>
                  </div>
                  <div className="tooltip-factor">
                    <span className="factor-icon">✓</span>
                    <span>{t('tooltipFactor4')}</span>
                  </div>
                </div>
                <div className="tooltip-scale">
                  <div className="scale-item high">
                    <span className="scale-dot high"></span>
                    <span>{t('tooltipScaleHigh')}</span>
                  </div>
                  <div className="scale-item medium">
                    <span className="scale-dot medium"></span>
                    <span>{t('tooltipScaleMedium')}</span>
                  </div>
                  <div className="scale-item low">
                    <span className="scale-dot low"></span>
                    <span>{t('tooltipScaleLow')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Options principales équilibrées */}
      <div className="main-actions-grid">
        {selectedNode ? (
          <>
            {/* Option 1: Analyse IA */}
            <button
              type="button"
              className="main-option-btn ai-analysis"
              onClick={() => handleActionWithConfirmation(saveNode, 'ai')}
              disabled={buttonDisabled}
            >
              {isLoadingState ? (
                <div className="loading-content">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">{t('loadingAiAnalysis')}</div>
                </div>
              ) : (
                <>
                  <div className="option-icon">🤖</div>
                  <div className="option-content">
                    <div className="option-title">{t('optionsAiAnalysisTitle')}</div>
                    <div className="option-description">{t('optionsAiAnalysisDescription')}</div>
                  </div>
                  <div className="option-arrow">→</div>
                </>
              )}
            </button>

            {/* Option 2: Mise à jour simple */}
            <button
              type="button"
              className="main-option-btn simple-update"
              onClick={() => handleActionWithConfirmation(updateNodeSimple, 'simple')}
              disabled={buttonDisabled}
            >
              {isLoadingState ? (
                <div className="loading-content">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">{t('loadingUpdating')}</div>
                </div>
              ) : (
                <>
                  <div className="option-icon">⚡</div>
                  <div className="option-content">
                    <div className="option-title">{t('optionsQuickUpdateTitle')}</div>
                    <div className="option-description">{t('optionsQuickUpdateDescription')}</div>
                  </div>
                  <div className="option-arrow">→</div>
                </>
              )}
            </button>
          </>
        ) : (
          /* Bouton de création directe quand aucun nœud n'est sélectionné */
          <button
            type="button"
            className="main-option-btn create-node"
            onClick={() => handleActionWithConfirmation(saveNode, 'create')}
            disabled={buttonDisabled}
            style={{ gridColumn: '1 / -1', maxWidth: '500px', margin: '0 auto' }}
          >
            {isLoadingState ? (
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <div className="loading-text">{t('creatingNode')}</div>
              </div>
            ) : (
              <>
                <div className="option-icon">💾</div>
                <div className="option-content">
                  <div className="option-title">{t('createNodeTitle')}</div>
                  <div className="option-description">{t('createNodeDescription')}</div>
                </div>
                <div className="option-arrow">→</div>
              </>
            )}
          </button>
        )}
      </div>

      {/* Actions secondaires intégrées */}
      <div className="secondary-actions">
        <button
          type="button"
          className="help-btn"
          onClick={() => setShowGuide(!showGuide)}
        >
          <span className="help-icon">❓</span>
          <span>{showGuide ? t('hideGuide') : t('workflowGuide')}</span>
        </button>

        {selectedNode && resetForm && (
          <button
            type="button"
            className="cancel-btn"
            onClick={resetForm}
            disabled={isLoadingState}
          >
            <span className="cancel-icon">↩️</span>
            <span>{t('cancelModifications')}</span>
          </button>
        )}
      </div>

      {/* Guide intégré */}
      {showGuide && (
        <div className="integrated-guide">
          <div className="guide-content-tabs">
            <div className="guide-tab active">
              <h3 className="guide-section-title">
                <span className="guide-icon">🤖</span>
                {t('aiAnalysisTitle')}
              </h3>
              <div className="guide-features">
                <div className="feature-item">
                  <span className="feature-icon">✅</span>
                  <span>{t('aiAnalysisFeature1')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✅</span>
                  <span>{t('aiAnalysisFeature2')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✅</span>
                  <span>{t('aiAnalysisFeature3')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✅</span>
                  <span>{t('aiAnalysisFeature4')}</span>
                </div>
              </div>
            </div>

            <div className="guide-tab">
              <h3 className="guide-section-title">
                <span className="guide-icon">⚡</span>
                {t('quickUpdateTitle')}
              </h3>
              <div className="guide-features">
                <div className="feature-item">
                  <span className="feature-icon">✅</span>
                  <span>{t('quickUpdateFeature1')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">⚠️</span>
                  <span>{t('quickUpdateFeature2')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">⚠️</span>
                  <span>{t('quickUpdateFeature3')}</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">💡</span>
                  <span>{t('quickUpdateFeature4')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-recommendations">
            <h4 className="recommendations-title">
              <span className="rec-icon">💡</span>
              {t('recommendationsTitle')}
            </h4>
            <div className="recommendations-grid">
              <div className="rec-card ai-rec">
                <h5>{t('recommendationsUseAI')}</h5>
                <ul>
                  <li>{t('recommendationsAIOption1')}</li>
                  <li>{t('recommendationsAIOption2')}</li>
                  <li>{t('recommendationsAIOption3')}</li>
                  <li>{t('recommendationsAIOption4')}</li>
                </ul>
              </div>
              <div className="rec-card simple-rec">
                <h5>{t('recommendationsUseQuick')}</h5>
                <ul>
                  <li>{t('recommendationsQuickOption1')}</li>
                  <li>{t('recommendationsQuickOption2')}</li>
                  <li>{t('recommendationsQuickOption3')}</li>
                  <li>{t('recommendationsQuickOption4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interface de confirmation élégante */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-header">
              <div className="warning-icon-large">⚠️</div>
              <h3 className="confirmation-title">{t('confirmationTitle')}</h3>
            </div>
            
            <div className="confirmation-content">
              <p className="confirmation-message">
                {t('confirmationMessage')}
              </p>
              
              <div className="impact-details">
                <h4>{t('confirmationImpactsDetected')}</h4>
                <ul>
                  {selectedNode?.type !== nodeType && (
                    <li>🔄 {t('confirmationTypeChange')}</li>
                  )}
                  {selectedNode?.name !== nodeName && (
                    <li>📝 {t('confirmationNameChange')}</li>
                  )}
                  {['Core', 'API', 'Database', 'Authentication'].includes(selectedNode?.type) && (
                    <li>🚨 {t('confirmationCriticalNode')}</li>
                  )}
                </ul>
              </div>

              <div className="recommendation-box">
                <strong>💡 {t('confirmationRecommendation')}</strong>
              </div>
            </div>
            
            <div className="confirmation-actions">
              <button
                type="button"
                className="confirm-btn-cancel"
                onClick={cancelAction}
              >
                <span>❌</span>
                <span>{t('confirmationCancel')}</span>
              </button>
              
              <button
                type="button"
                className="confirm-btn-ai"
                onClick={() => {
                  cancelAction();
                  handleActionWithConfirmation(actionContext.primaryAction, 'ai');
                }}
              >
                <span>🤖</span>
                <span>{t('confirmationUseAI')}</span>
              </button>
              
              <button
                type="button"
                className="confirm-btn-proceed"
                onClick={confirmAction}
              >
                <span>⚡</span>
                <span>{t('confirmationProceedWithout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartActionButton;
